/** Add, edit or delete one entry of a tenant's AI vocabulary **as support** (source `ops`;
 *  `console_ai_vocabulary_manage`). The backend runs the same proposal, preview and checks as the
 *  MCP tool `define_term` and applies the edit at once as ONE revertible change set — on
 *  production too, never pending. Editing the company's entry makes it support's.
 *
 *  An edit sends only the fields that change (the binding whole, when it changes); nothing
 *  changed sends nothing. A refusal keeps the dialog open with the backend's words: `invalid`
 *  (an unknown concept, a value that is not the company's, one word twice for one concept — its
 *  `errors`), a field error, `change_sets_disabled` or the change engine's gates (409), or a
 *  404 when the entry is gone (the tab reloads). */
import { zodResolver } from '@hookform/resolvers/zod';
import {
  Alert,
  Autocomplete,
  Box,
  Button,
  Checkbox,
  FormControlLabel,
  ListItemText,
  MenuItem,
  Stack,
  TextField,
  Typography,
} from '@mui/material';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { useMemo } from 'react';
import { Controller, useForm, useWatch } from 'react-hook-form';
import { useNavigate } from 'react-router-dom';
import { z } from 'zod';

import { aiChangeKeys, shortId } from '@/api/aiChanges';
import {
  BINDING_LABELS,
  VOCABULARY_LIMITS as L,
  compactBinding,
  editEntry,
  fieldErrorsOf,
  vocabularyErrorOf,
  vocabularyKeys,
  type ConsoleAIVocabulary,
  type VocabularyBinding,
  type VocabularyEdit,
  type VocabularyEditFields,
  type VocabularyEditResult,
  type VocabularyEntry,
} from '@/api/aiVocabulary';
import { ConsoleApiError } from '@/api/client';
import { rbacApi } from '@/api/rbac';
import * as regionsApi from '@/api/regions';
import { AppDialog } from '@/components/AppDialog';
import { useSnackbar } from '@/components/Snackbar';
import { UntrustedInline } from '@/components/UntrustedText';
import { aiChangeHref } from '@/features/ai-changes/aiChangeLinks';
import { usePermissions } from '@/hooks/usePermissions';

interface Named {
  id: number;
  name: string;
}

/** "One per line" → the words, trimmed, blank lines and repeats dropped. */
export function parseAliases(text: string): string[] {
  const out: string[] = [];
  for (const line of text.split('\n')) {
    const word = line.trim();
    if (word && !out.includes(word)) out.push(word);
  }
  return out;
}

/** "ar, br pe" → ["AR", "BR", "PE"]. */
export function parseCountries(text: string): string[] {
  return [...new Set(text.split(/[\s,;]+/).map((c) => c.trim().toUpperCase()).filter(Boolean))];
}

const reasonRule = z
  .string()
  .trim()
  .min(L.reasonMin, `Say why (at least ${L.reasonMin} characters) — it is the change set’s reason.`)
  .max(L.reasonMax, `At most ${L.reasonMax} characters.`);

const FormSchema = z
  .object({
    term: z.string().trim().min(1, 'The word or phrase the company uses.').max(L.term, `At most ${L.term} characters.`),
    concept: z.string().min(1, 'Pick the concept it stands for.'),
    aliases: z.string(),
    definition: z.string().max(L.definition, `At most ${L.definition} characters.`),
    role_ids: z.array(z.number()),
    region_ids: z.array(z.number()),
    countries: z.string(),
    tag_category: z.string(),
    booking_system: z.string(),
    channel: z.string(),
    language: z.string(),
    default: z.boolean(),
    reason: reasonRule,
  })
  .superRefine((v, ctx) => {
    const words = parseAliases(v.aliases);
    if (words.length > L.aliases)
      ctx.addIssue({ code: 'custom', path: ['aliases'], message: `At most ${L.aliases} other words.` });
    if (words.some((w) => w.length > L.alias))
      ctx.addIssue({ code: 'custom', path: ['aliases'], message: `Each at most ${L.alias} characters.` });
  });
type FormValues = z.infer<typeof FormSchema>;

function defaultsOf(entry: VocabularyEntry | null, term: string): FormValues {
  const b = entry?.binding ?? {};
  return {
    term: entry?.term ?? term,
    concept: entry?.concept ?? '',
    aliases: (entry?.aliases ?? []).join('\n'),
    definition: entry?.definition ?? '',
    role_ids: b.role_ids ?? [],
    region_ids: b.region_ids ?? [],
    countries: (b.countries ?? []).join(', '),
    tag_category: b.tag_category ?? '',
    booking_system: b.booking_system ?? '',
    channel: b.channel ?? '',
    language: b.language ?? '',
    default: Boolean(b.default),
    reason: '',
  };
}

function bindingOf(v: FormValues): VocabularyBinding {
  return compactBinding({
    role_ids: v.role_ids,
    region_ids: v.region_ids,
    countries: parseCountries(v.countries),
    tag_category: v.tag_category.trim(),
    booking_system: v.booking_system.trim(),
    channel: v.channel.trim(),
    language: v.language.trim(),
    default: v.default,
  });
}

/** A binding in one canonical form for comparing: keys in order, lists sorted. */
function canonical(b: VocabularyBinding): string {
  const c = compactBinding(b) as Record<string, unknown>;
  return JSON.stringify(
    Object.fromEntries(
      Object.entries(c).map(([k, v]) => [k, Array.isArray(v) ? [...(v as (string | number)[])].sort() : v]),
    ),
  );
}

/** What the edit sends: everything given for a new entry, the changed fields for an existing one. */
export function editFields(entry: VocabularyEntry | null, v: FormValues): VocabularyEditFields {
  const term = v.term.trim();
  const aliases = parseAliases(v.aliases);
  const definition = v.definition.trim();
  const binding = bindingOf(v);
  if (!entry) {
    return {
      term,
      concept: v.concept,
      ...(aliases.length ? { aliases } : {}),
      ...(definition ? { definition } : {}),
      ...(Object.keys(binding).length ? { binding } : {}),
    };
  }
  const out: VocabularyEditFields = {};
  if (term !== entry.term) out.term = term;
  if (v.concept !== entry.concept) out.concept = v.concept;
  if (JSON.stringify(aliases) !== JSON.stringify(entry.aliases)) out.aliases = aliases;
  if (definition !== entry.definition) out.definition = definition;
  if (canonical(binding) !== canonical(entry.binding)) out.binding = binding;
  return out;
}

/** The refusal in words: the backend's detail and its errors, a field error, or the transport's. */
function refusalOf(error: unknown): { detail: string; errors: string[] } | null {
  if (!error) return null;
  if (error instanceof ConsoleApiError) {
    const coded = vocabularyErrorOf(error.body);
    if (coded) return { detail: coded.detail, errors: coded.errors ?? [] };
    if (error.status === 404)
      return { detail: 'This entry is no longer one of the company’s — the list was reloaded.', errors: [] };
    if (error.status === 403)
      return { detail: 'Editing the vocabulary needs console_ai_vocabulary_manage.', errors: [] };
    const fields = fieldErrorsOf(error.body);
    if (fields.length) return { detail: 'The edit was refused.', errors: fields };
  }
  return { detail: error instanceof Error ? error.message : 'The edit failed.', errors: [] };
}

function useApply(topId: number, onApplied: (r: VocabularyEditResult) => void) {
  const qc = useQueryClient();
  const snackbar = useSnackbar();
  const navigate = useNavigate();
  const { hasPerm } = usePermissions();
  return useMutation({
    mutationFn: (body: VocabularyEdit) => editEntry(topId, body),
    onSuccess: (result) => {
      void qc.invalidateQueries({ queryKey: vocabularyKeys.tab(topId) });
      void qc.invalidateQueries({ queryKey: aiChangeKeys.all });
      snackbar.show(
        `Applied as one change set (${shortId(result.change_set)}) — revertible on this tab.`,
        'success',
        {
          autoHideDuration: 15_000,
          ...(hasPerm('console_ai_changes_view')
            ? {
                action: {
                  label: 'Open the change set',
                  onClick: () => navigate(aiChangeHref(result.change_set)),
                },
              }
            : {}),
        },
      );
      onApplied(result);
    },
    onError: (error) => {
      if (error instanceof ConsoleApiError && error.status === 404)
        void qc.invalidateQueries({ queryKey: vocabularyKeys.tab(topId) });
    },
  });
}

function RefusalAlert({ error }: { error: unknown }) {
  const refusal = refusalOf(error);
  if (!refusal) return null;
  return (
    <Alert severity="error" data-edit-refused>
      <Typography variant="body2">{refusal.detail}</Typography>
      {refusal.errors.length > 0 && (
        <Box component="ul" sx={{ m: 0, mt: 0.5, pl: 2.5 }}>
          {refusal.errors.map((e) => (
            <li key={e}>
              <Typography variant="body2">{e}</Typography>
            </li>
          ))}
        </Box>
      )}
    </Alert>
  );
}

export function VocabularyEntryDialog({
  topId,
  data,
  entry,
  initialTerm = '',
  onClose,
  onApplied,
}: {
  topId: number;
  data: ConsoleAIVocabulary;
  /** The entry to edit; null adds one. */
  entry: VocabularyEntry | null;
  /** A new entry's term (from Knowledge gaps' "Define this word"). */
  initialTerm?: string;
  onClose: () => void;
  onApplied: (result: VocabularyEditResult) => void;
}) {
  const { control, register, handleSubmit, formState } = useForm<FormValues>({
    resolver: zodResolver(FormSchema),
    defaultValues: defaultsOf(entry, initialTerm),
  });
  const { errors } = formState;
  const values = useWatch({ control }) as FormValues;
  const fields = editFields(entry, values);
  const nothingChanged = entry !== null && Object.keys(fields).length === 0;
  const reasonOk = (values.reason ?? '').trim().length >= L.reasonMin;

  const mutation = useApply(topId, (result) => {
    onApplied(result);
    onClose();
  });

  // The company's own staff roles and regions to bind to; a binding to one that is gone keeps
  // its id (`#id`) so it can be seen and removed.
  const roles = useQuery({
    queryKey: [...vocabularyKeys.tab(topId), 'roleOptions'],
    queryFn: () => rbacApi.listEmployeeRoles(topId),
    select: (r) => r.results.map((x) => ({ id: x.id, name: x.name })),
  });
  const regions = useQuery({
    queryKey: [...vocabularyKeys.tab(topId), 'regionOptions'],
    queryFn: () => regionsApi.list({ tour_operator_id: topId, pageSize: 500 }),
    select: (r) => r.results.map((x) => ({ id: x.id, name: x.name })),
  });
  const known = useMemo(() => {
    const names = new Map<number, string>();
    for (const n of entry?.binding_names?.role_ids ?? []) names.set(n.id, n.name);
    for (const n of entry?.binding_names?.region_ids ?? []) names.set(n.id, n.name);
    for (const n of roles.data ?? []) names.set(n.id, n.name);
    for (const n of regions.data ?? []) names.set(n.id, n.name);
    return names;
  }, [entry, roles.data, regions.data]);

  const submit = handleSubmit((v) => {
    if (mutation.isPending) return;
    const body: VocabularyEdit = entry
      ? { action: 'update', entry: entry.id, fields: editFields(entry, v), reason: v.reason.trim() }
      : { action: 'create', fields: editFields(null, v), reason: v.reason.trim() };
    mutation.mutate(body);
  });

  const idPicker = (
    name: 'role_ids' | 'region_ids',
    options: Named[] | undefined,
    failed: boolean,
  ) => (
    <Controller
      control={control}
      name={name}
      render={({ field }) => {
        const all = [...(options ?? [])];
        for (const id of field.value) if (!all.some((o) => o.id === id)) all.push({ id, name: known.get(id) ?? `#${id}` });
        return (
          <Autocomplete
            multiple
            size="small"
            options={all.map((o) => o.id)}
            value={field.value}
            onChange={(_, next) => field.onChange(next)}
            getOptionLabel={(id) => known.get(id) ?? `#${id}`}
            renderInput={(params) => (
              <TextField
                {...params}
                label={BINDING_LABELS[name]}
                helperText={
                  failed
                    ? `Couldn’t list the company’s ${name === 'role_ids' ? 'staff roles' : 'regions'}; the ones bound stay.`
                    : data.binding_keys[name]
                }
              />
            )}
          />
        );
      }}
    />
  );

  const text = (name: 'tag_category' | 'booking_system' | 'channel' | 'language' | 'countries') => (
    <TextField
      size="small"
      label={BINDING_LABELS[name]}
      helperText={data.binding_keys[name]}
      {...register(name)}
      fullWidth
    />
  );

  return (
    <AppDialog
      open
      onClose={onClose}
      size="md"
      disableBackdropClose
      closeDisabled={mutation.isPending}
      title={entry ? 'Edit an entry' : 'Add an entry'}
      actions={
        <>
          {nothingChanged && (
            <Typography variant="body2" color="text.secondary" sx={{ mr: 'auto', pl: 1 }}>
              Nothing changed yet.
            </Typography>
          )}
          <Button onClick={onClose} disabled={mutation.isPending}>
            Cancel
          </Button>
          <Button
            variant="contained"
            disabled={mutation.isPending || nothingChanged || !reasonOk}
            onClick={() => void submit()}
          >
            {mutation.isPending ? 'Applying…' : 'Apply'}
          </Button>
        </>
      }
    >
      <Stack spacing={2} sx={{ mt: 1 }}>
        <Typography variant="body2" color="text.secondary">
          {entry?.origin === 'tenant'
            ? 'The company wrote this entry: once support edits it, it is support’s. '
            : ''}
          Applies at once as one change set of the company, revertible on this tab — on production
          too, never pending. Only the AI’s knowledge tools read it: nothing guests see changes,
          and never who counts as a guide.
        </Typography>
        {entry && (
          <Typography variant="body2" color="text.secondary" component="div">
            Now: <UntrustedInline text={entry.term} /> as {entry.concept_name}.
          </Typography>
        )}
        <TextField
          label="Term"
          required
          size="small"
          {...register('term')}
          error={Boolean(errors.term)}
          helperText={errors.term?.message ?? 'The word or phrase the company uses.'}
          inputProps={{ maxLength: L.term }}
        />
        <Controller
          control={control}
          name="concept"
          render={({ field }) => (
            <TextField
              select
              required
              size="small"
              label="Concept"
              value={field.value}
              onChange={field.onChange}
              error={Boolean(errors.concept)}
              helperText={errors.concept?.message ?? 'Which of our concepts the term stands for.'}
            >
              {data.concepts.map((c) => (
                <MenuItem key={c.key} value={c.key}>
                  <ListItemText primary={c.name} secondary={c.key} />
                </MenuItem>
              ))}
            </TextField>
          )}
        />
        <TextField
          label="Other words"
          size="small"
          multiline
          minRows={2}
          {...register('aliases')}
          error={Boolean(errors.aliases)}
          helperText={errors.aliases?.message ?? `One per line, at most ${L.aliases}.`}
        />
        <TextField
          label="Definition"
          size="small"
          multiline
          minRows={2}
          {...register('definition')}
          error={Boolean(errors.definition)}
          helperText={errors.definition?.message ?? 'What the company means by it, in its words.'}
          inputProps={{ maxLength: L.definition }}
        />

        <Box component="fieldset" sx={{ border: 1, borderColor: 'divider', borderRadius: 1, p: 1.5, m: 0 }}>
          <Typography component="legend" variant="caption" color="text.secondary" sx={{ px: 0.5 }}>
            Bound to (optional): which of the company’s own values the term means
          </Typography>
          <Stack spacing={1.5}>
            {idPicker('role_ids', roles.data, roles.isError)}
            {idPicker('region_ids', regions.data, regions.isError)}
            <Box sx={{ display: 'grid', gridTemplateColumns: { xs: '1fr', sm: '1fr 1fr' }, gap: 1.5 }}>
              {text('countries')}
              {text('tag_category')}
              {text('booking_system')}
              {text('channel')}
              {text('language')}
              <Controller
                control={control}
                name="default"
                render={({ field }) => (
                  <FormControlLabel
                    control={
                      <Checkbox
                        size="small"
                        checked={field.value}
                        onChange={(e) => field.onChange(e.target.checked)}
                      />
                    }
                    label="The default language"
                  />
                )}
              />
            </Box>
          </Stack>
        </Box>

        <TextField
          label="Reason"
          required
          size="small"
          multiline
          minRows={2}
          {...register('reason')}
          error={Boolean(errors.reason)}
          helperText={errors.reason?.message ?? 'Required — becomes the change set’s reason.'}
          inputProps={{ maxLength: L.reasonMax }}
        />
        <RefusalAlert error={mutation.error} />
      </Stack>
    </AppDialog>
  );
}

const DeleteSchema = z.object({ reason: reasonRule });

export function VocabularyDeleteDialog({
  topId,
  entry,
  onClose,
  onApplied,
}: {
  topId: number;
  entry: VocabularyEntry;
  onClose: () => void;
  onApplied: (result: VocabularyEditResult) => void;
}) {
  const { register, handleSubmit, control, formState } = useForm<{ reason: string }>({
    resolver: zodResolver(DeleteSchema),
    defaultValues: { reason: '' },
  });
  const reason = useWatch({ control, name: 'reason' }) ?? '';
  const mutation = useApply(topId, (result) => {
    onApplied(result);
    onClose();
  });
  const submit = handleSubmit((v) => {
    if (mutation.isPending) return;
    mutation.mutate({ action: 'delete', entry: entry.id, reason: v.reason.trim() });
  });
  return (
    <AppDialog
      open
      onClose={onClose}
      size="sm"
      disableBackdropClose
      closeDisabled={mutation.isPending}
      title="Delete this entry"
      actions={
        <>
          <Button onClick={onClose} disabled={mutation.isPending}>
            Cancel
          </Button>
          <Button
            variant="contained"
            color="error"
            disabled={mutation.isPending || reason.trim().length < L.reasonMin}
            onClick={() => void submit()}
          >
            {mutation.isPending ? 'Deleting…' : 'Delete'}
          </Button>
        </>
      }
    >
      <Stack spacing={2} sx={{ mt: 1 }}>
        <Typography variant="body2" component="div">
          <UntrustedInline text={entry.term} /> ({entry.concept_name}) goes from the company’s
          vocabulary at once, as one change set — revert it on this tab to bring the entry back.
        </Typography>
        <TextField
          label="Reason"
          required
          size="small"
          multiline
          minRows={2}
          {...register('reason')}
          error={Boolean(formState.errors.reason)}
          helperText={formState.errors.reason?.message ?? 'Required — becomes the change set’s reason.'}
          inputProps={{ maxLength: L.reasonMax }}
        />
        <RefusalAlert error={mutation.error} />
      </Stack>
    </AppDialog>
  );
}
